import React from "react";
import get from "lodash/get";
import { connect } from "react-redux";
import {
  Alert,
  Icon,
  Layout,
  Table,
  Button,
  Modal,
  Input,
  Badge,
  Tooltip,
  Switch,
  message,
  Select,
} from "antd";
import moment from "moment";

import AdminHeader from "../../components/AdminHeader/AdminHeader";
import Footer from "../../components/Footer/Footer";
import classes from "./FinancePricesEditor.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  activateFinancePriceRequest,
  deleteFinancePriceRequest,
  getFinanceEventsList,
  getFinancePricesSearchRequest,
  getSitesRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getFinanceEventCreatePermission,
  getFinanceEventDeletePermission,
  getFinanceEventUpdatePermission,
} from "../../reducers/user/selectors";
import FinancePriceForm from "../../components/FinancePriceForm/FinancePriceForm";
import { withNamespaces } from "react-i18next";

const { Content } = Layout;
const { Option } = Select;

class FinancePricesEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      prices: [],
      sites: [],
      nameSearch: null,
      errorMessage: null,
      responseMessage: null,
      isLoading: false,
      site: null,
      order: "asc",
      currentPage: 1,
      total: 1,
      pageSize: 25,
      eventVisibleDropdown: false,
      eventsSelected: {},
      sortedInfo: {},
      priceEdit: null,
      approvedSelected: null,
      filter: null,
      event_id: null,
      eventList: [],
    };
  }

  async componentDidMount() {
    this.onNameSearch();
    this.getFinanceEventList();

    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) {
      if (get(e, 'response.status') === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }
  }

  getFinanceEventList = async () => {
    const { data } = await getFinanceEventsList();
    this.setState({
      eventList: data,
    });
  };

  onNameSearch = async (value) => {
    this.setState({ nameSearch: value, eventsSelected: {}, currentPage: 1 }, this.getPrices);
  };

  getPrices = async (
    page = this.state.currentPage,
    per_page = this.state.pageSize
  ) => {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getFinancePricesSearchRequest({
        order_direction: this.state.order,
        page,
        per_page,
        order_column: get(this.state, "sortedInfo.columnKey"),
        filter_by: this.state.filter,
        event_id: this.state.event_id,
      });

      if (Array.isArray(data.data)) {
        this.setState({
          prices: data.data,
          total: data.total_pages * per_page,
          currentPage: page,
        });
      } else {
        this.setState({ prices: data, total: 1 * per_page, currentPage: page });
      }
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        prices: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  };

  onNameChange = (e) => {
    this.setState({ nameSearch: e.target.value });
  };

  handleTableChange = (pagination, filter, sorter) => {
    this.setState(
      {
        isLoading: true,
        pageSize: pagination.pageSize,
        sortedInfo: sorter,
        order:
          get(sorter, "order") === "ascend"
            ? "asc"
            : get(sorter, "order") === "descend"
            ? "desc"
            : "asc",
      },
      () => this.getPrices(pagination.current, pagination.pageSize)
    );
  };

  onPriceEdit = (item) => () => {
    this.setState({ priceEdit: item });
  };

  onPriceEditHide = () => {
    this.setState({ priceEdit: null });
  };

  onPriceEditSubmit = () => {
    this.setState({ priceEdit: null }, this.getPrices);
  };

  addPrice = () => {
    this.setState({ priceEdit: {} });
  };

  clearFilters = () => {
    this.setState(
      { nameSearch: null, filter: null, event_id: null, currentPage: 1 },
      this.getPrices
    );
  };

  onSwitchPriceActivation = (id, state) => async () => {
    try {
      if (state === "active") {
        await deleteFinancePriceRequest(id);
      } else {
        await activateFinancePriceRequest(id);
      }

      message.success(this.props.i18n.t("Saved"));

      this.getPrices();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error("Error");
      console.log(e);
    }
  };

  handleFilterChange = (value) => {
    this.setState(
      {
        filter: value,
        event_id: null,
        eventsSelected: {},
        sortedInfo: null,
        currentPage: 1
      },
      this.getPrices
    );
  };

  handleMilestoneChange = (value) => {
    this.setState({ event_id: value, sortedInfo: null, currentPage: 1 }, this.getPrices);
  };

  render() {
    const {
      errorMessage,
      isLoading,
      responseMessage,
      filter,
      sites,
      prices,
      currentPage,
      total,
      pageSize,
      event_id,
      priceEdit,
      eventList,
      sortedInfo,
    } = this.state;
    const { financeCreatePermission } = this.props;

    const columns = [
      {
        dataIndex: "event_name",
        key: "event_name",
        title: this.props.i18n.t("Event name"),
        render: (val, record) => (
          <div className={classes.nameWrapper}>
            <div style={{ width: "86px" }}>
              <Badge
                count={record.state}
                style={{
                  backgroundColor:
                    record.state === "active" ? "#52c41a" : "#777",
                }}
              />
              <br />
            </div>
            {val}
          </div>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "event_name" && sortedInfo.order,
      },
      {
        dataIndex: "price",
        key: "price",
        title: this.props.i18n.t("Value"),
        render: (val, record) => (
          <div>
            {val} {record.price_currency}
          </div>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "price" && sortedInfo.order,
      },
      {
        dataIndex: "price_type",
        key: "price_type",
        title: this.props.i18n.t("NamespaceFinancePricesEditor:Price type"),
        render: (val, record) => (
          <div>
            {val}{" "}
            {val === "per_site" &&
            ` (${get(
              this.state.sites.find(({ id }) => id === record.site_id),
              "name",
              this.props.i18n.t("NamespaceFinancePricesEditor:Site not found")
            )})`}
          </div>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "price_type" && sortedInfo.order,
      },
      {
        dataIndex: "created",
        key: "created",
        title: this.props.i18n.t("Created"),
        render: (val) => moment(val).format("YYYY-MM-DD HH:mm"),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "created" && sortedInfo.order,
      },
      {
        dataIndex: "id",
        key: "id",
        title: this.props.i18n.t("Actions"),
        render: (val, item) => (
          <div className={classes.actions}>
            <Tooltip
              placement="topLeft"
              title={this.props.i18n.t(
                "NamespaceFinancePricesEditor:Edit Price"
              )}
            >
              <Icon
                type="edit"
                theme="filled"
                className={classes.iconEdit}
                onClick={this.onPriceEdit(item)}
              />
            </Tooltip>
            &nbsp; &nbsp;
            <div style={{ width: "90px", display: "flex" }}>
              {((this.props.financeUpdatePermission &&
                item.state === "inactive") ||
                (this.props.financeDeletePermission &&
                  item.state === "active")) && (
                <div>
                  <b className={classes.switchLabel}>
                    {this.props.i18n.t("State")}
                  </b>
                  <br />
                  <Tooltip
                    placement="topLeft"
                    title={this.props.i18n.t(
                      "NamespaceFinancePricesEditor:Activate / Deactivate Price"
                    )}
                  >
                    <Switch
                      className={classes.reactivateBtn}
                      checked={item.state === "active"}
                      onChange={this.onSwitchPriceActivation(val, item.state)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        ),
      },
    ];

    return (
      <div>
        <Layout className={classes.parentContainer}>

          <Content className={classes.content}>
            <h3 className="headline-default">{this.props.i18n.t("Prices")}</h3>

            <div className={classes.searchWrapperFilter}>
              <label className={classes.searchWrapperFilterItem}>
                <b className={classes.searchWrapperLabel}>
                  {this.props.i18n.t("Filter by")}
                </b>
                <Select
                  onChange={this.handleFilterChange}
                  value={filter}
                  style={{ width: "100%" }}
                  placeholder={this.props.i18n.t("Select")}
                >
                  <Option value="Event">
                    {this.props.i18n.t("Event")}
                  </Option>
                </Select>
              </label>

              {filter === "Event" && (
                <label className={classes.searchWrapperFilterItem}>
                  <b className={classes.searchWrapperLabel}>
                    {this.props.i18n.t("Event")}
                  </b>
                  <Select
                    onChange={this.handleMilestoneChange}
                    value={event_id}
                    style={{ width: "100%" }}
                    placeholder={this.props.i18n.t("Select")}
                    showSearch
                    optionFilterProp="children"
                    filterOption
                  >
                    {eventList.map(({ id, name }) => (
                      <Option value={id} key={id}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </label>
              )}

              <span className={classes.clearButton} onClick={this.clearFilters}>
                {this.props.i18n.t("Clear")}
              </span>
            </div>

            {errorMessage && (
              <Alert
                type="error"
                message={errorMessage}
                className={classes.alert}
              />
            )}

            {responseMessage && (
              <Alert
                type="success"
                message={responseMessage}
                className={classes.alert}
              />
            )}

            {financeCreatePermission && (
              <div>
                <Button type="primary" onClick={this.addPrice}>
                  {this.props.i18n.t(
                    "NamespaceFinancePricesEditor:Add new price"
                  )}
                </Button>
                <br />
                <br />
              </div>
            )}

            <Table
              columns={columns}
              dataSource={prices}
              loading={isLoading}
              rowKey="id"
              onChange={this.handleTableChange}
              pagination={{
                onShowSizeChange: (current, size) => {
                  this.setState({ pageSize: size });
                },
                current: currentPage,
                total,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                position: 'both',
              }}
            />

            {/*{isLoading && <Icon type="loading" />}*/}

            <Modal
              visible={!!priceEdit}
              onCancel={this.onPriceEditHide}
              footer={null}
            >
              {priceEdit && (
                <FinancePriceForm
                  {...priceEdit}
                  isNew={priceEdit && !priceEdit.id}
                  onSubmit={this.onPriceEditSubmit}
                  onCancel={this.onPriceEditHide}
                  sites={sites}
                />
              )}
            </Modal>
          </Content>

        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  financeCreatePermission: getFinanceEventCreatePermission(state),
  financeDeletePermission: getFinanceEventDeletePermission(state),
  financeUpdatePermission: getFinanceEventUpdatePermission(state),
});
const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withNamespaces()(FinancePricesEditor))
);



// WEBPACK FOOTER //
// ./src/containers/FinancePricesEditor/FinancePricesEditor.js